/******************************************************************************
 * @Author                : Jbristhuille<jbristhuille@gmail.com>              *
 * @CreatedDate           : Invalid Date                                      *
 * @LastEditors           : Jbristhuille<jbristhuille@gmail.com>              *
 * @LastEditDate          : 2025-03-06 14:23:24                               *
 *****************************************************************************/

/* SUMMARY
  * Imports
  * Components
  * Styles
  * Moment config
  * PlanningCalendar: display the planning calendar section
    * onSelectTimeSlot: function to call when a time slot is selected
    * onSelectEvent: function to call when an event is selected
    * startAccessor: get the start date of an event
    * endAccessor: get the end date of an event
    * titleAccessor: get the title of an event
    * hourRangeFormat: format the hour range
    * getEventColor: get the color of an event
*/

/* Imports */
import { Calendar, DateRange, momentLocalizer, NavigateAction, SlotInfo, View } from "react-big-calendar";
import moment from "moment";
import "moment/locale/fr";
import "moment/locale/nl";
import "moment/locale/it";
import { useEffect, useMemo, useState } from "react";
import "react-big-calendar/lib/css/react-big-calendar.css";
/***/

/* Services */
import { color } from "../../../../services/colors/colors";
/***/

/* Components */
import Event from "./planning-components/Event";
/***/

/* Styles */
import "./PlanningCalendar.scss";
import { startOfMonth } from "../../../../services/utils/utils";
import CalendarToolBar from "./planning-components/CalendarToolBar";
import Header from "./planning-components/Header";
import { lastHoveredDayEnd } from "./slotSelection";
/***/

const SUPPORTED_CALENDAR_LOCALES: Record<string, string> = {
  fr: "fr",
  nl: "nl",
  it: "it",
  en: "en"
};

const resolveCalendarLocale = (rawLocale: string | null | undefined): string => {
  if (!rawLocale) return "en";
  const base = rawLocale.split("-")[0].toLowerCase();
  return SUPPORTED_CALENDAR_LOCALES[base] ?? "en";
};

const createLocalizer = () => momentLocalizer(moment);
/***/

/**
* PlanningCalendar: display the planning calendar section
* @param times - the list of times to display
* @param onTimeClick - the function to call when a time is clicked
* @param onEventClick - the function to call when an event is clicked
* @param className - additional class name
*/
export default function PlanningCalendar ({
  times,
  loading,
  onPlanningNavigate,
  onTimeClick,
  onEventClick,
  unscheduledWorkordersLength, 
  unasignedWorkordersLength, 
  drawerType, 
  setDrawerType,
  filterDrawerComponent,
  filterBadgesComponent,
  className
}: {
  times: EasyTech.Time[],
  loading: boolean,
  onPlanningNavigate?: (newDate: Date, view: View) => void,
  onTimeClick?: (info: {start: Date, end: Date}) => void,
  onEventClick?: (e: EasyTech.Time) => void,
  unscheduledWorkordersLength: number, 
  unasignedWorkordersLength: number, 
  drawerType: string | null, 
  setDrawerType: (type: string | null) => void,
  filterDrawerComponent: JSX.Element,
  filterBadgesComponent: JSX.Element,
  className?: string
}) {
  const [timeState, setTimeState] = useState(new Date(new Date().setMinutes(0, 0, 0)));
  const [actualDate, setActualDateState] = useState(new Date(new Date().setMinutes(0, 0, 0)));
  const [currentView, setCurrentView] = useState<View>("week");

  const hasCompletedRealDates = (event: EasyTech.Time) => {
    return Boolean(event.workOrder?.time?.start && event.workOrder?.time?.end);
  };

  const resolveLocale = (): string => {
    if (typeof window === "undefined") return "en";
    const stored = window.localStorage?.getItem("i18nextLng") || "en";
    return resolveCalendarLocale(stored);
  };

  const [calendarLocale, setCalendarLocale] = useState<string>(() => {
    const locale = resolveLocale();
    moment.locale(locale);
    if (moment.localeData(locale)) {
      moment.updateLocale(locale, {
        week: {
          dow: 1,
          doy: 1
        }
      });
    }
    return locale;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const updateLocaleFromStorage = () => {
      const locale = resolveLocale();
      moment.locale(locale);
      if (moment.localeData(locale)) {
        moment.updateLocale(locale, {
          week: {
            dow: 1,
            doy: 1
          }
        });
      }
      setCalendarLocale(locale);
    };

    updateLocaleFromStorage();
    window.addEventListener("storage", updateLocaleFromStorage);

    return () => {
      window.removeEventListener("storage", updateLocaleFromStorage);
    };
  }, []);

  const localizer = useMemo(() => createLocalizer(), []);

  const calendarMessages = useMemo(() => {
    switch (calendarLocale) {
    case "fr":
      return {
        date: "Date",
        time: "Heure",
        event: "Événement",
        allDay: "Toute la journée",
        week: "Semaine",
        work_week: "Semaine ouvrée",
        day: "Jour",
        month: "Mois",
        previous: "Précédent",
        next: "Suivant",
        yesterday: "Hier",
        tomorrow: "Demain",
        today: "Aujourd'hui",
        agenda: "Agenda",
        noEventsInRange: "Aucun événement sur cette période",
        showMore: (total: number) => `+ ${total} de plus`
      };
    case "nl":
      return {
        date: "Datum",
        time: "Tijd",
        event: "Evenement",
        allDay: "Hele dag",
        week: "Week",
        work_week: "Werkweek",
        day: "Dag",
        month: "Maand",
        previous: "Vorige",
        next: "Volgende",
        yesterday: "Gisteren",
        tomorrow: "Morgen",
        today: "Vandaag",
        agenda: "Agenda",
        noEventsInRange: "Geen evenementen in deze periode",
        showMore: (total: number) => `+ ${total} extra`
      };
    case "it":
      return {
        date: "Data",
        time: "Ora",
        event: "Evento",
        allDay: "Tutto il giorno",
        week: "Settimana",
        work_week: "Settimana lavorativa",
        day: "Giorno",
        month: "Mese",
        previous: "Precedente",
        next: "Successivo",
        yesterday: "Ieri",
        tomorrow: "Domani",
        today: "Oggi",
        agenda: "Agenda",
        noEventsInRange: "Nessun evento in questo intervallo",
        showMore: (total: number) => `+ altri ${total}`
      };
    default:
      return {
        date: "Date",
        time: "Time",
        event: "Event",
        allDay: "All day",
        week: "Week",
        work_week: "Work week",
        day: "Day",
        month: "Month",
        previous: "Back",
        next: "Next",
        yesterday: "Yesterday",
        tomorrow: "Tomorrow",
        today: "Today",
        agenda: "Agenda",
        noEventsInRange: "No events in this range",
        showMore: (total: number) => `+ ${total} more`
      };
    }
  }, [calendarLocale]);

  /**
  * onNavigate: function to call when navigate in planning
  * @param newDate - the slot information 
  * @param view - the slot information 
  * @param action - the slot information 
  */
  const onNavigate = (newDate: Date, view: View, action: NavigateAction) => {
    setActualDateState(newDate);
    setCurrentView(view);
  };
  /***/

  /**
  * onView: function to call when navigate in planning
  * @param view - the slot information 
  */
  const onView = (view: View) => {
    setCurrentView(view);
    setActualDateState(actualDate);
  };
  /***/

  /**
  * onSelectTimeSlot: function to call when a time slot is selected
  * @param slotInfo - the slot information 
  */
  const onSelectTimeSlot = (slotInfo: SlotInfo) => {
    if(onTimeClick) onTimeClick({
      start: slotInfo.start,
      end: lastHoveredDayEnd(slotInfo.end)
    });
    setTimeState(new Date(moment(slotInfo.start).format("x")));
  };
  /***/

  /**
  * onSelectEvent: function to call when an event is selected
  * @param event - event item 
  */
  const onSelectEvent = (event: EasyTech.Time) => {
    if(onEventClick) onEventClick(event);
    if (event.date_from) setTimeState(new Date(event.date_from));
  };
  /***/

  /**
  * startAccessor: get the start date of an event
  * @param event - the event item 
  */
  const startAccessor = (event: EasyTech.Time) => {
    // Use real dates only when both real start and real end are filled
    if (hasCompletedRealDates(event)) {
      return new Date(event.workOrder!.time!.start);
    }

    return new Date(event.date_from);
  };
  /***/

  /**
  * endAccessor: get the end date of an event
  * @param event - the event item 
  */
  const endAccessor = (event: EasyTech.Time) => {
    // Use real dates only when both real start and real end are filled
    if (hasCompletedRealDates(event)) {
      return new Date(event.workOrder!.time!.end);
    }

    return new Date(event.date_to);
  };
  /***/

  /**
  * titleAccessor: get the title of an event
  * @param event - the event item 
  */
  const titleAccessor = (event: EasyTech.Time) => {
    return event.type.label;
  };
  /***/

  /**
  * houreRangeFormat: format the hour range
  * @param range - the date range
  * @returns - the formatted string 
  */
  const hourRangeFormat = (range: DateRange) => {
    let ret = range.start.getHours().toString().padStart(2, "0")+":";
    ret += range.start.getMinutes().toString().padStart(2, "0")+" - ";
    ret += range.end.getHours().toString().padStart(2, "0")+":";
    ret += range.end.getMinutes().toString().padStart(2, "0");
    return ret;
  };
  /***/

  /**
  * getEventColor: get the color of an event
  * @param event - the event item
  * @returns - the color string 
  */
  const getEventColor = (event: EasyTech.Time) => {
    if (event.technician) {
      if (event.technician.attributes) {
        if (event.workOrder)
          return color.getColor(event.technician.attributes.color, "500");
        else
          return color.getColor(event.technician.attributes.color, "300");
      } else {
        if (event.workOrder) return "gray";
        else return "lightgray";
      }
    } else {
      return "grey";
    }
  };
  /***/

  
  /**
  * getEventColor: get the color of an event
  * @param event - the event item
  * @returns - the color string 
  */
  const getEventBgColor = (event: EasyTech.Time) => {
    if (event.technician) {
      if (event.technician.attributes) {
        if (event.workOrder)
          return color.getColor(event.technician.attributes.color, "100");
        else
          return color.getColor(event.technician.attributes.color, "50");
      } else {
        if (event.workOrder) return "lightgray";
        else return "whitesmoke";
      }
    } else {
      return "grey";
    }
  };
  /***/

  /**
  * useEffect: Get times from the API 
  */
  useEffect(() => {
    if(onPlanningNavigate)
      onPlanningNavigate(actualDate, currentView);
  }, [actualDate, currentView, onPlanningNavigate]);
  /***/

  return (
    <div className={`planning-calendar ${className ?? ""}`}>
      <Calendar
        localizer={localizer}
        culture={calendarLocale}
        messages={calendarMessages}
        startAccessor={startAccessor}
        endAccessor={endAccessor}
        titleAccessor={titleAccessor}
        selectable="ignoreEvents"
        views={["month", "week", "work_week", "day"]}
        defaultView="week"
        onNavigate={onNavigate}
        onView={onView}
        formats={{
          weekdayFormat: "dddd",
          dayFormat: "DD dddd",
          timeGutterFormat: "HH:mm",
          dateFormat: "DD",
          eventTimeRangeFormat: hourRangeFormat,
          selectRangeFormat: hourRangeFormat
        }}
        scrollToTime={timeState}
        step={30}
        timeslots={1}
        onSelectSlot={onSelectTimeSlot}
        onSelectEvent={onSelectEvent}
        eventPropGetter={(event) => {
          const eventColor = getEventColor(event);
          const isWorkorder = Boolean(event.workOrder);
          const isCompletedRealEvent = hasCompletedRealDates(event);
          const isScheduledWorkorder = isWorkorder && !isCompletedRealEvent;

          return {
            className: isCompletedRealEvent
              ? "planning-event-real"
              : isScheduledWorkorder
                ? "planning-event-scheduled"
                : "",
            style: {
              border: "1px solid " + eventColor,
              borderLeft: isWorkorder ? "0" : "5px solid " + eventColor,
              backgroundColor: getEventBgColor(event),
              "--event-accent-color": eventColor
            }
          };
        }}
        dayPropGetter={(date) => {
          return date.getDay() === 6 || date.getDay() === 0
            ? { style: { backgroundColor: "rgb(243 244 246)" } }
            : {};
        }}
        components={{
          event: Event,
          toolbar: (props) =>
            CalendarToolBar({
              loading,
              unasignedWorkordersLength,
              unscheduledWorkordersLength,
              drawerType,
              setDrawerType,
              filterDrawerComponent,
              filterBadgesComponent,
              ...props
            }),
          header: Header
        }}
        events={times}
      />
    </div>
  );
}
/***/
